import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useRef,
  useMemo,
} from 'react';
import { bool, func, object } from 'prop-types';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { MdCollections, MdClose } from 'react-icons/md';
import styled, { keyframes, css } from 'styled-components';
import PhoenixTheme from '@airasia/phoenix-components/components/Theme';
import UserReviewsRating from '@airasia/phoenix-components/components/UserReviewsRating';
import queryString from 'query-string';
import {
  translate,
  LocaleProviderCtx,
} from '@airasia-common/libraries/i18n/LocaleProvider';
import {
  getDisplayAddress,
  numberFormat,
  getDiscountPercentageLocale,
} from '@airasia-common/libraries/i18n/LocaleHelper';
import {
  getFilteredRooms,
  getRoomFilterFunctions,
} from '@airasia-common/libraries/utilities/RoomFiltersHelper';
import {
  BACK_FROM_GUEST_DETAIL_LABEL,
  CANCELLATION_REFUND_TYPE,
  CANCELLATION_PENALTY_TYPES,
  roomFilterOptions,
} from '@airasia-common/libraries/constants';
import { createExtraRoomLevelAmenitiesMobile } from '@airasia-common/libraries/utilities/AmenityHelper';
import Wrapper from '@airasia-common/react-dom/components/Wrapper/Wrapper';
import {
  H2,
  H3,
  SubBodyText,
} from '@airasia-common/react-dom/components/Text/Heading';
import Breadcrumbs from '@airasia-common/react-dom/components/Breadcrumbs';
import Image from '@airasia-common/react-dom/components/Image';
import Row from '@airasia-common/react-dom/components/Container/Row';
import Location from '@airasia-common/react-dom/components/Location';
import EmptyStateWrapper from '@airasia-common/react-dom/components/EmptyState/EmptyStateWrapper';
import { HeadHelmet } from '@airasia-common/react-dom';
import {
  TYPE as AmenitiesType,
  AmenitiesList,
} from '@airasia-common/react-dom/components/Amenity/AmenitiesList';

import StarRating from '@airasia-common/react-dom/components/StarRating';
import Text from '@airasia-common/react-dom/components/Text/Text';
import Container from '@airasia-common/react-dom/components/Container/Container';
import Modal from '@airasia-common/react-dom/components/Modal/Modal';
import HotelGenericImage from '@airasia-common/react-dom/components/Image/HotelGenericImage';
import NewRoomCard from '@airasia/phoenix-widgets/widgets/NewRoomCard';
import {
  LOB_HOTEL,
  HOTEL_DETAIL_PAGE,
} from '@airasia-hotels/core/redux/analytics/analytics.constants';
import {
  discountText,
  percentPromotion,
} from '@airasia-common/libraries/utilities/formatters';
import { isLoggedUser } from '@airasia-common/libraries/utilities/CredentialHelper';
import { rateWithSelectedBedGroupId } from '@airasia-hotels/core/helpers';
import Cookies from 'js-cookie';
import {
  formatLongerLocalTime,
  countNights,
} from '@airasia-common/libraries/datetime';
import ScrollUpButton from '@airasia/phoenix-components/components/ScrollUpButton';
import LoadingWrapper from '@airasia-common/react-dom/components/Wrapper/LoadingWrapper';

import Divider from '@airasia-common/react-dom/components/Divider';
import HotelSwitch from '@airasia-common/react-dom/components/HotelSwitch';

import loadable from '@loadable/component';
import loadableVisibility from 'react-loadable-visibility/loadable-components';
import LazyLoad from 'react-lazyload';
import UserReviewOverview from '@airasia-common/react-dom/components/UserReview/UserReviewOverview';
import Column from '@airasia-common/react-dom/components/Container/Column';
import isEmpty from 'lodash/isEmpty';
import { constants, useWindowSize } from '../../../utils';
import { fetchSEOSchema, populateSchema } from '../../../utils/seoUtils';
import setDocumentTitle from '../../../utils/setDocumentTitle';
import { LowInventoryFOMOmsg } from '../../../utils/LowInventoryFOMOmsg';
import { isFromWechat } from '../../../utils/WechatUtil';
import {
  getSSOInfo,
  getSSORefreshAccessToken,
  useCredential,
} from '../../../hooks/useCredential';
import { HotelsSummaryHeader } from '../../../components';
import { navigateToHome } from '../../../utils/navigation';
import usePerformanceTiming from '../../../hooks/usePerformanceTiming';
import FallBackMessage from '../../../components/FallBackMessage';
import {
  processGalleryV2,
  getTranslatedPricebreakdownData,
} from '../../../utils/Utils';
import {
  useFlagHotelEnableTripadvisorRating,
  useFlagVoucherBanner,
  useFlagEnableFomoMessagesWeb,
  useFlagEnableHotelBogoPromo,
  useFlagHotelLastRoomFomo,
  useFlagHotelLastRoomCountFomo,
  useFlagBookingDoneFomo,
  useFlagFastFillingFomo,
  useFlagHotelPriceDropFomo,
  useFlagEnableHotelBogoPromoDiscountId,
  useFlagGoogleMapsColored,
  useFlagHotelPaynowPromoIds,
  useFlagRebrandEnabled,
  useFlagEnableSubscriptionBannerWeb,
  useFlagEnableRM9Button,
  useFlagEnableRM9SpecialCurrency,
  useEnablePreCheckoutCall,
  useEnableSEOData,
  useEnableBlockedRate,
  useEnableSendingMFTParameter,
  useFlagEnableExtraRoomAmenities,
} from '../../../hooks/useFeatureFlag';
import useLocalStorage from '../../../hooks/useLocalStorage';
// import StaticMapWithAddress from '../../../components/StaticMapWithAddress';
import { formatPayLaterDate } from '../../../utils/PayLaterDateFormat';
import {
  PAY_LATER_FORMAT_SHORT_TIME,
  PAY_LATER_FORMAT_TIME,
  ROBOTS_NO,
  ROBOTS_YES,
  SUBSCRIPTION,
  SOLD_OUT,
} from '../../../utils/constants';
import useUpdateAndFetchRefreshToken from '../../../hooks/useUpdateAndFetchRefreshToken';

const PreCheckout = loadableVisibility(() =>
  import(/* webpackChunkName:"PreCheckout" */ '../../../components/PreCheckout')
);

const RoomMapWithPOI = loadableVisibility(() =>
  import(
    /* webpackChunkName:"RoomMapWithPOI" */ '@airasia-common/react-dom/components/RoomMapWithPOI/'
  )
);
const POIModalContent = loadableVisibility(() =>
  import(/* webpackChunkName:"POIModalContent" */ './POIModalContent')
);
const ShimmerHotelCard = loadable(() =>
  import(
    /* webpackChunkName:"ShimmerHotelCard" */ '@airasia-common/react-dom/components/HotelCard/ShimmerHotelCard'
  )
);

const DescriptionAndPolicies = loadableVisibility(() =>
  import(
    /* webpackChunkName:"DescriptionAndPolicies" */ '@airasia-common/react-dom/components/DescriptionAndPolicies/DescriptionAndPolicies'
  )
);

const NotificationBannerInline = loadableVisibility(() =>
  import(
    /* webpackChunkName:"NotificationBannerInline" */ '@airasia/phoenix-components/components/NotificationBannerInline'
  )
);

const FeedbackWrapper = loadableVisibility(() =>
  import(
    /* webpackChunkName:"FeedbackWrapper" */ '../../../components/FeedbackWrapper'
  )
);

const MapModal = loadableVisibility(() =>
  import(/* webpackChunkName:"MapModal" */ './MapModal')
);
const RoomCardV2 = loadable(() =>
  import(
    /* webpackChunkName:"RoomCardV2" */ '@airasia/phoenix-widgets/widgets/RoomCard'
  )
);
const BigPointsSuccessCard = loadableVisibility(() =>
  import(
    /* webpackChunkName:"BigPointsSuccessCard" */ '../../../components/BigPointsSuccessCard'
  )
);
const TripAdvisor = loadableVisibility(() =>
  import(
    /* webpackChunkName:"TripAdvisor" */ '@airasia-common/react-dom/components/TripAdvisor'
  )
);

const ImageGalleryDesktop = loadableVisibility(() =>
  import(
    /* webpackChunkName:"ImageGalleryDesktop" */ '@airasia/phoenix-widgets/widgets/ImageGalleryV2/ImageGalleryDesktop'
  )
);

const ImageGalleryMobile = loadableVisibility(() =>
  import(
    /* webpackChunkName:"ImageGalleryMobile" */ '@airasia/phoenix-widgets/widgets/ImageGalleryV2/ImageGalleryMobile'
  )
);

const ImageGallery = loadableVisibility(() =>
  import(
    /* webpackChunkName:"ImageGallery" */ '@airasia/phoenix-widgets/widgets/ImageGallery'
  )
);

const GuestReviews = loadableVisibility(() =>
  import(
    /* webpackChunkName: "GuestReviews" */ '../../../components/GuestReviews'
  )
);

const RoomFilters = loadableVisibility(() =>
  import(
    /* webpackChunkName:"RoomFilters" */ '@airasia/phoenix-widgets/widgets/RoomFilters'
  )
);

const MAX_OPTIONS_VIEW_SCROLL_UP = 10;
const SCROLL_POINT_VIEW_SCROLL_UP = 50;

const fadeIn = keyframes`
  0%   { opacity: 0;}
  25%  { opacity: 0.25;}
  50%  { opacity: 0.5;}
  100% { opacity: 1; }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const Debug = styled.div`
  display: inline;
  padding-left: 5px;
  font-weight: normal;
  font-size: 12px;
  color: #4c4c50;
`;

const ScrollUpBtnStyle = styled(Wrapper)`
  visibility: ${({ show }) => (show ? 'none' : 'hidden')};
  animation: ${({ show }) =>
    show
      ? css`
          ${fadeIn} 0.5s linear;
        `
      : css`
          ${fadeOut} 2s linear;
        `};
`;

const HeroImageContainer = styled(Wrapper)`
  cursor: pointer;
  position: relative;
`;

const CollectionsIcon = styled(MdCollections)`
  margin-right: 4px;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.primaryWhite};
`;

const CloseIcon = styled(MdClose)`
  margin-right: -4px;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.primaryWhite};
  cursor: pointer;
`;

const ImageCount = styled(Wrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  flex-direction: row;
  background: #171717;
  border-radius: 50px;
  width: 77px;
  position: absolute;
  z-index: 0;
  top: 16px;
  color: #ffffff;
  font-size: 12px;
  line-height: 18px;
`;

const GalleryContainer = styled(Wrapper)`
  background: ${({ theme }) => theme.colors.darkBlack};
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`;

const GalleryV2Container = styled(Wrapper)`
  background: ${({ theme }) => theme.colors.darkBlack};
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.neutral};
`;

const GalleryV2MobileContainer = styled(Wrapper)`
  background-color: ${({ theme }) => theme.colors.primaryWhite};
  height: 100vh;
`;

const GalleryHeader = styled(Wrapper)`
  background: ${({ theme }) => theme.colors.darkBlack};
  width: 100vw;
`;

const Gallery = styled(ImageGallery)`
  flex-grow: 1;
  img {
    object-fit: cover;
  }
  > div {
    margin: 0 auto 16px auto;
  }
`;

const ShowMapView = styled(Text)`
  padding-left: 5px;
  color: ${({ move }) => (move ? '#8D1414' : '#4b4fa6')};
  display: inline-block;
  fontsize: 14px;
  lineheight: 16px;
  cursor: pointer;
  font-weight: 500;
`;

//colorcode update with featureflag #MOVE

const HotelsSummaryHeaderWrapper = styled.div`
  ${({ isMobileView }) => {
    return isMobileView ? '' : 'min-height: 90px';
  }};
`;

const CertifiedSuper = styled.div`
  position: absolute;
  z-index: 1;
  ${({ isMobileView }) => {
    return isMobileView ? 'top: 13px' : 'top: 12px';
  }};
  ${({ isMobileView }) => {
    return isMobileView ? 'left: 0' : 'left: 12px';
  }};
`;

const VoucherBannerContainer = styled(Wrapper)`
  width: 1224px;
  height: 59px;

  background: #587591;
  border-radius: 4px;
`;

const VoucherMessage = styled(Wrapper)`
  width: 778px;
  height: 62px;

  ${({ isMobileView }) => {
    return isMobileView ? 'margin-left: -14%;' : 'margin-left: -14%;';
  }};
  font-family: 'DM Sans';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;

  display: flex;
  align-items: center;

  color: #ffffff;
`;

const VoucherImage = styled(Wrapper)`
  ${({ isMobileView }) => {
    return isMobileView ? 'width: 100%;' : 'width: 25%';
  }};
  margin-left: 20px;
`;

const DescriptionText = styled(Text)`
  color: #4c4c50;
  font-size: 14px;
  line-height: 22px;
  margin-bottom: 5px;
  font-weight: 400;
  margin-top: 12px;
`;

const HotelDetail = ({
  filter,
  hotelDetail,
  isFetching,
  fetchHotelDetail,
  fetchGuestReview,
  fetchHotelRooms,
  clearHotelDetail,
  isFailed,
  trackMobileBackButton,
  trackHotelShowMoreDescriptionClicked,
  trackHotelShowMorePolicyClicked,
  trackProductToCheckout,
  trackReceivedFirstByte,
  trackFirstRender,
  trackContentDownloadCompleted,
  featureFlags,
  updateHotelFilter,
  defaultLocaleUsed,
  updateHotelDetail,
  trackHotelSearchWidget,
  uhswData,
  updateBigPointFilter,
  bigMembership,
  bigpoints,
  guestReviews,
  trackHotelSeeOnAMapWeb,
  trackHotelShowMapWeb,
  trackHotelMapSelectARoomWeb,
  trackErrorPopOutRoom,
  trackViewImageBanner,
  trackViewRoom,
  trackHotelDetailedReview,
  trackHotelDetailedReviewPagination,
  trackHotelReviewClick,
  trackHotelRoomFilterUse,
  fetchUserSubscription,
  subscription,
  fetchPreCheckout,
  preCheckout,
  refreshToken,
  fetchRefreshToken,
}) => {
  const { hotelId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const credential = useCredential();
  const { isMobileView } = useWindowSize();
  const polyglot = useContext(LocaleProviderCtx);
  const roomListRef = useRef();
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [scrollToRoomList, setScrollToRoomList] = useState(false);
  const [hotelDetailState, setHotelDetailState] = useState();
  const [isFetchedHotelDetail, setIsFetchedHotelDetail] = useState(false);
  const { timeToFirstByte, timeToFirstRender } = usePerformanceTiming();
  const [isShowRecommended, setIsShowRecommended] = useState(false);
  const lemonAgentToken = Cookies.get('lemonAgent');
  const [isShowFallBackMessage, setIsShowFallBackMessage] = useState(true);
  const [pageY, setPageY] = useState(window.pageYOffset);
  const [countOpts, setCountOpts] = useState(0);
  const [width, setWidth] = React.useState(window.innerWidth);
  const [rightPosCrollUpBtn, setRightPosCrollUpBtn] = useState('32px');
  const isShowTripadvisorRating = useFlagHotelEnableTripadvisorRating();
  const [localStoreState] = useLocalStorage('current_hotel_local', {});
  const [selectedHotelName] = useLocalStorage('current_hotel_name', '');
  const [showVoucherBanner, setshowVoucherBanner] = useState(false);
  const [voucherMessage, setShowVoucherMessage] = useState('');
  const enableVoucherBanner = useFlagVoucherBanner();
  const enableFomoMessagesWeb = useFlagEnableFomoMessagesWeb();
  const configLastRoomCountFomo = useFlagHotelLastRoomCountFomo();
  const configPriceDropFomo = useFlagHotelPriceDropFomo();
  const configLastRoomFomo = useFlagHotelLastRoomFomo();
  const configBookingDoneFomo = useFlagBookingDoneFomo();
  const configFastFillingFomo = useFlagFastFillingFomo();
  const isEnableHotelBogoPromo = useFlagEnableHotelBogoPromo();
  const BogoPromoDiscountId = useFlagEnableHotelBogoPromoDiscountId();
  const GoogleMapsColoredFlag = useFlagGoogleMapsColored();
  const enableRM9Button = useFlagEnableRM9Button();
  const paynowPromoIds = useFlagHotelPaynowPromoIds();
  const enableRebranding = useFlagRebrandEnabled();
  const enableFetchUserSubscription = useFlagEnableSubscriptionBannerWeb();
  const rm9SpecialCurrency = useFlagEnableRM9SpecialCurrency();
  const enablePreCheckoutCall = useEnablePreCheckoutCall();
  const enableSendingMFTParameter = useEnableSendingMFTParameter();
  const isExtraRoomAmenitiesEnabled = useFlagEnableExtraRoomAmenities();
  const [displayBigPointSuccessCard, setDisplayBigPointSuccessCard] = useState(
    false
  );
  const [galleryRoomDetail, setGalleryRoomDetail] = useState();
  const [roomMapWithPOIProps, setRoomMapWithPOIProps] = useState({
    isMobileView,
    mapProps: null,
    poiProps: null,
  });
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isPOIModalOpen, setIsPOIModalOpen] = useState(false);
  const [viewRoomCard, setViewRoomCard] = useState(true); // triger view room card event only once.
  const [viewGuestReviews, setViewGuestReviews] = useState(true);
  const { state = {} } = useLocation();
  const { param_soldOutSearchCriteria } = state;
  const [soldOutTokens, setSoldOutTokens] = useState([]);
  const [selectedBedGroups, setSelectedBedGroups] = useState(null);

  const channel = queryString.parse(window.location.search)?.channel;
  const initialSoldOut = param_soldOutSearchCriteria
    ? param_soldOutSearchCriteria
    : [];
  const [soldOutSearchCriteria, setSoldOutSearchCriteria] = useState(
    initialSoldOut
  );
  const [initialSearchedFilter, setInitialSearchedFilter] = useState(null);
  const enableSEOData = useEnableSEOData();
  const scheamURL = '/hotels/seo/schema/schema.json';
  const url = `${
    process.env.REACT_APP_NEW_METADATA_BASE_URL
  }${scheamURL}?${new Date()}`;

  const [schemaseo, setScheamseo] = useState({});
  const [isSeoDatePushedToHead, setSeoDatePushedToHead] = useState(false);

  const isBlockedRateIsEnabled = useEnableBlockedRate();

  const getMFTParams = () => {
    // get mft param from url
    const mftParam = queryString.parse(window.location.search)?.mft;

    // get first mft param if got multiple mft params in url.
    const isMftParamArray = Array.isArray(mftParam);
    if (mftParam) {
      sessionStorage.setItem('mft', mftParam);
    }
    const mftValue = mftParam || sessionStorage.getItem('mft');
    const mft = isMftParamArray ? mftParam[0] : mftValue;
    return mft;
  };

  useEffect(() => {
    async function fetchData() {
      const response = await fetchSEOSchema(url);
      setScheamseo(response);
    }
    enableSEOData && fetchData();
  }, []);

  useEffect(() => {
    if (
      enableSEOData &&
      !isSeoDatePushedToHead &&
      hotelDetail &&
      hotelDetail?.rooms &&
      hotelDetail?.rooms?.length
    ) {
      setSeoDatePushedToHead(true);
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(populateSchema(schemaseo, hotelDetail));

      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [hotelDetail]);

  const extractTokens = (soldOutSearchCriteria, hotelDetails) => {
    // Initialize an array to store the soldOutToken
    const soldOutToken = [];

    if (hotelDetails && Array.isArray(hotelDetails.rooms)) {
      // Iterate through each criteria and find the corresponding token
      for (const criteria of soldOutSearchCriteria) {
        const { roomId, rateId, rateOptionId } = criteria;

        // Find the room in hotelDetails
        const room = hotelDetails.rooms.find(r => r.id === roomId);

        if (room) {
          // Find the rate in the room
          const rate = room.rates.find(
            r => r.id === rateId && r.rateOptionId === rateOptionId
          );

          if (rate && rate.bedGroups.length > 0) {
            // Add the token to the array
            soldOutToken.push(rate.bedGroups[0].token);
          }
        }
      }
    }
    return soldOutToken;
  };
  const getRM9PriceBreakdownData = useCallback(secondNnight => {
    if (secondNnight && Object.keys(secondNnight).length) {
      const objGtm = {
        event: 'trackEventNoEcommerce',
        'eventDetails.category': 'roomcard|RM9',
        'eventDetails.action': `hover`,
        'eventDetails.label': `expand`,
      };
      trackHotelSearchWidget(objGtm);

      return getTranslatedPricebreakdownData(
        secondNnight,
        translate,
        filter?.languageId,
        rm9SpecialCurrency?.search(secondNnight?.savings?.currency) === -1
          ? false
          : true
      );
    }
    return {};
  }, []);

  const [showSoldOutMessage, setShowSoldOutMessage] = useState(false);
  const [
    isSoldOutMessageAlreadyShown,
    setSoldOutMessageAlreadyShown,
  ] = useState(false);

  const extractSearchedFilterData = filter => {
    if (!filter) {
      return {
        checkin: '',
        checkout: '',
        locationId: '',
        adults: '',
        children: '',
        roomsCount: 0,
      };
    }

    const result = {
      checkin: filter.checkin || '',
      checkout: filter.checkout || '',
      locationId: filter.locationId || '',
      adults: filter.adults || '',
      children: filter.children || '',
      roomsCount: filter.roomsCount || 0,
    };

    return result;
  };

  const compareSearchedFilterObjects = (obj1, obj2) => {
    if (!obj1 || !obj2) {
      return false; // Return false if either obj1 or obj2 is null/undefined
    }
    return (
      obj1.checkin === obj2.checkin &&
      obj1.checkout === obj2.checkout &&
      obj1.roomsCount === obj2.roomsCount &&
      obj1.adults === obj2.adults &&
      obj1.children === obj2.children &&
      obj1.locationId === obj2.locationId
    );
  };

  const handleSoldOutSearchCriteria = (listingParams, initialFilter) => {
    const extractedFilterData = extractSearchedFilterData(listingParams);

    if (
      initialSoldOut.length > 0 &&
      compareSearchedFilterObjects(extractedFilterData, initialFilter)
    ) {
      setSoldOutSearchCriteria(initialSoldOut);
    } else {
      setSoldOutSearchCriteria([]);
    }
    setSoldOutMessageAlreadyShown(false);
  };

  const isFilterInit = useMemo(() => !!filter.isInit, [filter.isInit]);

  const {
    id,
    image,
    name,
    address = {},
    location: { coordinates = {} } = {},
    description = { overview: '' },
    checkinDetails = {},
    checkoutDetails = {},
    policies = {},
    fees = {},
    starRating = 0,
    debug,
    guestRating,
    guestRatingCount,
    amenitiesRating,
    cleanlinessRating,
    comfortRating,
    conditionRating,
    locationRating,
    neighbourhoodRating,
    qualityRating,
    serviceRating,
    valueRating,
    guestRatingLabelId,
    amenities,
    topNotification,
    gallery = [],
    galleryV2 = [],
    tripadvisor,
    pointsOfInterest,
  } = hotelDetailState ? hotelDetailState : hotelDetail;
  const { rooms, isEmptyHotelRoom } = hotelDetail;
  const locationText = getDisplayAddress(address);
  const hasImageGallery = gallery.length > 1;
  const isLoggedIn = isLoggedUser(credential);
  const applyBigpoints = isLoggedIn ? bigpoints.applyBigpoints : false;

  const hotelName = name ? name : selectedHotelName;

  const selectedHotel = {
    id: id,
    name: name,
    baseRate: rooms?.[0]?.rates?.[0]?.baseRate,
    strikethroughPrice: rooms?.[0]?.rates?.[0]?.strikethroughPrice,
    image: image,
    starRating: starRating,
    guestRating: guestRating,
    guestRatingLabelId: guestRatingLabelId,
    guestRatingCount: guestRatingCount,
    aboveNameLabels: rooms?.[0]?.rates?.[0]?.isFreeCancellation
      ? [
          {
            translationId: 'tags.free_cancellation',
          },
        ]
      : [],
    ...(rooms?.[0]?.rates?.[0]?.availableRooms && {
      rightPillTexts: [
        {
          translationId: 'tags.rooms_left',
          numberOfRooms: rooms[0]?.rates[0]?.availableRooms,
        },
      ],
    }),
    recommended: rooms?.[0]?.rates?.[0]?.isRecommended,
    bigPoint: rooms?.[0]?.rates?.[0]?.big_life,
    isPayAtHotel: rooms?.[0]?.rates?.[0]?.isPayAtHotel,
    isCrossSellRate: rooms?.[0]?.rates?.[0]?.isCrossSellRate,
    images: processGalleryV2(galleryV2),
    coordinates: {
      lat: coordinates?.latitude,
      lon: coordinates?.longitude,
    },
    isPayLater: rooms?.[0]?.rates?.[0]?.isPayLater,
  };

  let hotels;
  hotels = localStoreState.hotels;
  const hotel = hotels ? hotels.find(e => e.id === hotelId) : selectedHotel;
  const navigateBack = () => {
    if (!isMobileView && !location.state) {
      navigateToHome();
    } else {
      history.goBack();
    }
  };

  const isPayNowMessageEnabled = useMemo(() => {
    // Check if the item's ID is listed in the 'paynowPromoIds' string.
    if (typeof paynowPromoIds === 'string' && paynowPromoIds !== '') {
      const isIDFound = paynowPromoIds
        .trim()
        .split(',')
        .find(paynowPromoId => paynowPromoId === hotelId);

      // If the item's ID is found in the 'paynowPromoIds', return the "Pay Now" message.
      return Boolean(isIDFound);
    }

    return false;
  }, [paynowPromoIds, hotelId]);

  const sleepsSectionCount =
    rooms &&
    rooms.length > 0 &&
    rooms.filter(
      item =>
        item.occupancy &&
        Object.keys(item.occupancy).length > 0 &&
        (item.occupancy.adults !== '0' || item.occupancy.children !== '0')
    );
  const showSleepsSection = sleepsSectionCount && sleepsSectionCount.length > 0;
  const shortDescription = description.overview || description.location;
  const roomRates =
    rooms &&
    rooms.length > 0 &&
    rooms.map((room, index) => {
      return room.rates;
    });

  let amenitiesSection = [];
  roomRates &&
    roomRates.forEach(item => {
      item.map(res => {
        for (let key in res) {
          if (key === 'amenities') {
            amenitiesSection.push(res[key]);
          }
        }
      });
    });
  const merged = [].concat.apply([], amenitiesSection);
  const showAmenitiesSection = merged.length > 0;
  const ratingLabel = translate(`common.guestRating.${guestRatingLabelId}`, {
    _: '',
  });
  const allLabel = translate('common.all');
  const selectRoomLabel = translate('roomCard.selectRoom');

  const guestRatingProps = {
    rating: guestRating,
    reviewCount: guestRatingCount,
    showInline: true,
    ratingLabel,
  };

  const [reviewScroll, setReviewScroll] = useState(false);

  const guestReviewOverviewProps = {
    rating: guestRating,
    countLabel:
      translate('common.reviews', {
        smart_count: guestRatingCount,
        count: numberFormat(guestRatingCount),
      }) + '.',
    ratingLabel,
    readReviewsLabel: translate('hotelDetailsPage.readReviews'),
    handleReadReviews: () => {
      trackHotelReviewClick();
      const mobileHeaderOffset = isMobileView ? 32 : 0;
      setTimeout(
        () =>
          window.scrollTo({
            top: guestReview.current.offsetTop - mobileHeaderOffset,
            behavior: 'smooth',
          }),
        0
      );
      setTimeout(
        () =>
          window.scrollTo({
            top: guestReview.current.offsetTop - mobileHeaderOffset,
            behavior: 'smooth',
          }),
        2000
      );
    },
  };

  // Create a separate arrow function to calculate discountValue
  const calculateDiscountValue = (rate, filter) => {
    const { strikethroughPrice, baseRate } = rate;
    const percentageDiscount = strikethroughPrice
      ? percentPromotion(
          baseRate.value,
          strikethroughPrice ? strikethroughPrice.value : 0
        )
      : null;

    return percentageDiscount
      ? getDiscountPercentageLocale(percentageDiscount, filter.languageId)
      : null;
  };

  const assignDiscountValue = useMemo(() => {
    if (Array.isArray(rooms) && rooms.length > 0) {
      return rooms.map(room => {
        const rates = room.rates.map(rate => {
          const discountValue = calculateDiscountValue(rate, filter);
          return {
            ...rate,
            discountValue,
          };
        });

        room.rates = rates;
        return room;
      });
    } else {
      return {};
    }
  }, [rooms, filter]);

  if (reviewScroll) {
    trackHotelDetailedReview();
  }
  const guestReviewIsFailedAndIsFetching = useMemo(
    () => !guestReviews?.isFailed && !guestReviews?.isFetching,
    [guestReviews?.isFailed, guestReviews?.isFetching]
  );
  const guestReview = useRef(null);
  const userReviewRatingProps = {
    isMobile: isMobileView,
    rating: guestRating,
    count:
      translate('common.reviews', {
        smart_count: guestRatingCount,
        count: numberFormat(guestRatingCount),
      }) + '.',
    ratingLabel: ratingLabel,
    individualRatings: [
      cleanlinessRating
        ? { key: 'Cleanliness', value: cleanlinessRating }
        : null,
      serviceRating ? { key: 'Service', value: serviceRating } : null,
      comfortRating ? { key: 'Comfort', value: comfortRating } : null,
      conditionRating ? { key: 'Condition', value: conditionRating } : null,
      neighbourhoodRating
        ? { key: 'Neighbourhood', value: neighbourhoodRating }
        : null,
      amenitiesRating ? { key: 'Amenities', value: amenitiesRating } : null,
      locationRating ? { key: 'Location', value: locationRating } : null,
      qualityRating ? { key: 'Quality', value: qualityRating } : null,
      valueRating ? { key: 'Value', value: valueRating } : null,
    ],
  };

  const tripAdvisorData =
    isShowTripadvisorRating && tripadvisor && tripadvisor.id
      ? {
          rating: tripadvisor.rating,
          ratingImageUrl: tripadvisor.rating_image_url,
          reviewCount: parseInt(tripadvisor.count),
          rankingString: tripadvisor.ranking?.ranking_string,
          reviewUrl: tripadvisor.url,
          subratings:
            tripadvisor.subratings && tripadvisor.subratings.length > 1
              ? tripadvisor.subratings.map(rating => {
                  return {
                    rating_image_url: rating.rating_image_url,
                    value: rating.value,
                    localized_name: translate(`tripAdvisor.${rating.type}`),
                  };
                })
              : [],
        }
      : null;

  const [showRoomFilters, setShowRoomFilters] = useState(true);
  const [filteredRooms, setFilteredRooms] = useState(rooms);

  const getRoomFilterCount = roomFilter => {
    return getRoomFilterFunctions(rooms, roomFilter).length;
  };

  const roomFilterItems = Object.keys(roomFilterOptions).map(key => {
    let value = roomFilterOptions[key];
    return {
      id: value,
      name: translate(`roomFilters.filter.${value}`),
      count: getRoomFilterCount(value),
      isChecked: false,
    };
  });

  const roomFiltersSelectionHandler = selectedItems => {
    setFilteredRooms([]);
    let currentFilteredRooms = getFilteredRooms(rooms, selectedItems);
    selectedItems.forEach(item => {
      roomFilterUse(item);
    });
    setTimeout(() => {
      setFilteredRooms(currentFilteredRooms);
    }, 0);
  };
  useEffect(() => {
    // Set up the timer only when showSoldOutMessage is true
    if (filteredRooms && filteredRooms.length > 0) {
      if (
        !isSoldOutMessageAlreadyShown &&
        soldOutSearchCriteria &&
        soldOutSearchCriteria.length > 0
      ) {
        setShowSoldOutMessage(true);
        const timer = setTimeout(() => {
          setSoldOutMessageAlreadyShown(true);
          setShowSoldOutMessage(false);
        }, 5000); // 5000 milliseconds = 5 seconds
        // Clean up the timer when the component unmounts
        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [soldOutSearchCriteria, filteredRooms, isSoldOutMessageAlreadyShown]);
  useEffect(() => {
    if (bigMembership) {
      setDisplayBigPointSuccessCard(applyBigpoints);
    }
    if (applyBigpoints) {
      setTimeout(() => {
        setDisplayBigPointSuccessCard(false);
      }, 4000);
    }
  }, [bigMembership, applyBigpoints]);

  useEffect(() => {
    const mobileHeaderOffset = isMobileView ? 56 : 0;
    const offset = 10;
    if (scrollToRoomList) {
      window.scrollTo({
        top: roomListRef.current.offsetTop - offset - mobileHeaderOffset,
        behavior: 'smooth',
      });
      setScrollToRoomList(false);
    }
  }, [scrollToRoomList, setScrollToRoomList, isMobileView]);
  const updateWidthAndHeight = () => {
    setWidth(window.innerWidth);
  };

  const isInViewport = el => {
    if (el) {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    }
  };

  useEffect(() => {
    const guestReviewspoint = document.querySelector('.guest-reviews ');
    if (viewGuestReviews && isInViewport(guestReviewspoint)) {
      trackHotelDetailedReview();
      setViewGuestReviews(false);
    }
  }, [viewGuestReviews, pageY, trackHotelDetailedReview]);

  useEffect(() => {
    const guestReviewspoint = document.getElementById('.reviews_guest ');
    if (viewGuestReviews && isInViewport(guestReviewspoint)) {
      trackHotelDetailedReview();
    }
  }, [viewGuestReviews, pageY, trackHotelDetailedReview]);
  // reviews_guest
  useEffect(() => {
    const roomCard = document.querySelector('.lazyload-wrapper ');
    if (viewRoomCard && isInViewport(roomCard)) {
      trackViewRoom({
        event: 'trackEventNoEcommerce',
        'eventDetails.category': 'Hotels SA|Room Select heatmap',
        'eventDetails.action': 'view',
        'eventDetails.label': 'roomcard',
      });
      setViewRoomCard(false);
    }
  }, [viewRoomCard, pageY, trackViewRoom]);
  useEffect(() => {
    window.addEventListener('resize', updateWidthAndHeight);
    return () => window.removeEventListener('resize', updateWidthAndHeight);
  }, []);

  useEffect(() => {
    if (width > 1224 + 32) {
      let posRight = (width - 1224) / 2;
      if (posRight >= 92) {
        posRight = posRight - 92;
      } else {
        posRight = posRight / 2;
      }
      setRightPosCrollUpBtn(`${posRight}px`);
    } else if (width < 1024) {
      setRightPosCrollUpBtn('32px');
    }
  }, [width]);

  useEffect(() => {
    if (document.getElementById('airasia-sso-modal-wrapper-close-button'))
      document.getElementById('airasia-sso-modal-wrapper-close-button').click();
  }, []);

  const [navigationObject, setNavigationObject] = useState({});

  const onRoomSelect = data => {
    const {
      roomId,
      id: rateId,
      rateOptionId,
      bedGroups,
      cancelPenalties,
      //will get other Data for RM9
    } = data;
    const bedGroupId = bedGroups[0].id;
    const selectedRoom = hotelDetailState.rooms.find(
      room => room.id === roomId
    );
    const selectedRate = rateWithSelectedBedGroupId(
      selectedRoom.rates.find(
        rate => rate.id === rateId && rate.rateOptionId === rateOptionId
      ),
      bedGroupId
    );

    if (enablePreCheckoutCall && hotelDetail.id && roomId && filter.isInit) {
      fetchPreCheckout({
        roomId: roomId,
        hotelId: hotelDetail.id,
        token: bedGroups && bedGroups.length > 0 ? bedGroups[0].token : null,
        lang: filter.languageId,
      });
      setSelectedBedGroups(bedGroups);
    }
    trackProductToCheckout({
      memorizedHotel: hotelDetailState,
      memorizedFilter: filter,
      rate: selectedRate,
      room: selectedRoom,
      isLoggedInUser: isLoggedIn,
    });

    updateHotelDetail({ hotelDetail: { ...hotelDetailState, selectedRate } });
    const hotelURL = window.location.pathname;
    let newURL = `${constants.ROUTE.HOTEL.ROOT}/${hotelDetailState.id}/${roomId}/${rateId}/${bedGroupId}/${rateOptionId}`;
    if (channel) {
      newURL += `?channel=${channel}`;
    }

    if (enablePreCheckoutCall) {
      setNavigationObject({
        newURL,
        cancelPenalties,
        hotelURL,
        soldOutSearchCriteria,
      });
    } else {
      history.push(newURL, {
        cancelPenalties,
        hotelURL,
        soldOutSearchCriteria,
      });
    }
  };

  const countRoomOpts = () => {
    const { rooms } = hotelDetail || {};
    let countOps = 0;
    if (rooms)
      rooms.forEach(item => {
        if (item.rates) countOps += item.rates.length;
      });
    return countOps;
  };

  const handleScroll = () => {
    setPageY(window.pageYOffset);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
  }, []);

  useEffect(() => {
    if (
      enablePreCheckoutCall &&
      preCheckout?.data?.type === SOLD_OUT &&
      selectedBedGroups &&
      !preCheckout?.isFetching &&
      !preCheckout?.isFailed &&
      !preCheckout.priceChanged
    ) {
      const newToken = selectedBedGroups[0]?.token;
      setShowSoldOutMessage(true);

      // Timer to hide the sold-out message after 5 seconds
      const timer = setTimeout(() => {
        setShowSoldOutMessage(false);
      }, 5000); // 5000 milliseconds = 5 seconds

      // Update the soldOutTokens state
      setSoldOutTokens(prevTokens => {
        // Add the new token if it's not already included
        return prevTokens.includes(newToken)
          ? prevTokens
          : [...prevTokens, newToken];
      });
      // Clean up the timer when the component unmounts
      return () => {
        clearTimeout(timer);
      };
    }
  }, [
    enablePreCheckoutCall,
    preCheckout?.isFetching,
    selectedBedGroups,
    preCheckout?.data,
    preCheckout?.isFailed,
    preCheckout?.priceChanged,
  ]);

  // Effect check Analytic
  useEffect(() => {
    if (timeToFirstByte) {
      trackReceivedFirstByte({
        timeToFirstByte,
        lob: LOB_HOTEL,
        pageId: HOTEL_DETAIL_PAGE,
      });
    }
    if (timeToFirstRender) {
      trackFirstRender({
        timeToFirstRender,
        lob: LOB_HOTEL,
        pageId: HOTEL_DETAIL_PAGE,
      });
      trackViewImageBanner({
        event: 'trackEventNoEcommerce',
        'eventDetails.category': 'Hotels SA|Room Select heatmap',
        'eventDetails.action': 'view',
        'eventDetails.label': 'hotel banner',
      });
    }
  }, [
    timeToFirstByte,
    timeToFirstRender,
    trackReceivedFirstByte,
    trackFirstRender,
    trackViewImageBanner,
  ]);

  useEffect(() => {
    if (filter.isInit) {
      if (!initialSearchedFilter) {
        const extractedFilterData = extractSearchedFilterData(filter);
        setInitialSearchedFilter(extractedFilterData);
      }
    }
  }, [filter.isInit, initialSearchedFilter, filter, hotelId, hotelDetail]);

  const subscriptionData = localStorage.getItem(SUBSCRIPTION);

  useEffect(() => {
    if (filter.isInit) {
      const { accessToken, loyaltyId } = getSSOInfo() || {};
      if (
        accessToken &&
        subscription?.data === null &&
        !subscriptionData &&
        enableFetchUserSubscription
      ) {
        fetchUserSubscription({
          accessToken,
          loyaltyId: loyaltyId,
          isHotelListingPage: false,
        });
      }
    }
  }, [filter.isInit, subscription?.data, enableFetchUserSubscription]);

  useEffect(() => {
    if (filter.isInit && filter.selectedCurrency) {
      fetchHotelDetail(hotelId);
    }
  }, [filter.selectedCurrency]);

  useEffect(() => {
    if (!viewGuestReviews) {
      fetchGuestReview({
        hotelId,
        lang: filter.languageId || constants.LANGUAGE_DEFAULT,
      });
    }
  }, [viewGuestReviews, hotelId, filter.languageId, filter.selectedCurrency]);

  useEffect(() => {
    let currencies = ['MYR', 'IDR', 'THB', 'PHP', 'SGD'];

    if (currencies.includes(filter.currency)) {
      setshowVoucherBanner(true);
    } else {
      setshowVoucherBanner(false);
    }
  }, [filter.currency]);

  useEffect(() => {
    if (filter.isInit) {
      setHotelDetailState(hotelDetail);
      setIsFetchedHotelDetail(true);
      setCountOpts(countRoomOpts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelDetail, hotelId, filter.isInit]);

  useUpdateAndFetchRefreshToken(refreshToken, fetchRefreshToken);

  const onCallFetchHotelRooms = useCallback(() => {
    const { authInfo } = credential;
    const ISSOData = getSSOInfo();
    const newAccessToken = getSSORefreshAccessToken();
    const SSOInfo = getSSOInfo();
    const {
      accessToken: ssoAccessToken,
      loyaltyId,
      refreshToken: ssorefreshToken,
    } = isLoggedIn ? SSOInfo : {};

    // refreshToken is came from the store which is updating time to time to track the accessToken or update the token in backgroun
    // newAccessToken is came from localstorage where we are saving when clicking the room because we dont wanna put it in url
    // ssoAccessToken is came from localStorage which might be expired when pulling this
    const accessToken = refreshToken || newAccessToken || ssoAccessToken; // TODO: update name of refreshToken to accessToken

    if (isLoggedUser(credential) && accessToken) {
      const params = {
        accessToken,
        ssorefreshToken,
        hotelId,
        lemonAgentToken,
        loyaltyId,
        isWeb: true,
      };
      if (enableSendingMFTParameter) {
        params.mft = getMFTParams();
      }
      fetchHotelRooms(params);
      return;
    }

    if (!ISSOData) {
      if (localStorage.getItem(SUBSCRIPTION)) {
        localStorage.removeItem(SUBSCRIPTION);
      }
      const params = { hotelId, lemonAgentToken, ssorefreshToken, isWeb: true };
      if (enableSendingMFTParameter) {
        params.mft = getMFTParams();
      }
      fetchHotelRooms(params);
      return;
    }
    if (authInfo) {
      const params = { hotelId, lemonAgentToken, ssorefreshToken, isWeb: true };
      if (enableSendingMFTParameter) {
        params.mft = getMFTParams();
      }
      fetchHotelRooms(params);
    }
  }, [
    refreshToken,
    fetchHotelRooms,
    hotelId,
    credential,
    subscription?.data,
    subscriptionData,
    lemonAgentToken,
    isLoggedIn,
  ]);

  useEffect(() => {
    if (filter.isInit) {
      onCallFetchHotelRooms();
    }
  }, [filter.isInit, filter.selectedCurrency, isLoggedIn]);

  const helmet = useMemo(() => {
    if (hotelDetail?.name && hotelDetail?.address && hotelDetail?.starRating) {
      const metaKeywords = `${hotelDetail?.name}, book ${hotelDetail?.name}, ${hotelDetail?.name} discount, ${hotelDetail?.name} deal, ${hotelDetail?.name} room, ${hotelDetail?.name} rate , ${hotelDetail?.name} price`;
      const metaDescription = `Stay at ${hotelDetail?.name} in ${hotelDetail?.address?.city} with airasia Hotels! ${hotelDetail?.starRating} stars hotel, great location with superior, meet family needs & nice amenities. Book now!`;
      const pageTitle = `Book ${hotelDetail?.name} ${hotelDetail?.address?.city} with the best price | airasia hotels`;

      return (
        <HeadHelmet
          metaDescription={metaDescription}
          metaKeywords={metaKeywords}
          cannonicalUrl={'https://www.airasia.com' + window.location.pathname}
          metaRobots={ROBOTS_YES}
          title={pageTitle}
          alternateUrl={`https://www.airasia.com/hotel/${hotelDetail?.id}`}
        />
      );
    } else {
      return null;
    }
  }, [hotelDetail?.name, hotelDetail?.address, hotelDetail?.starRating]);

  const topMsg = topNotification && (
    <PhoenixTheme theme={appEnabledTheme}>
      <NotificationBannerInline
        accessible
        closable={false}
        message={translate(topNotification.messageId)}
        showLeftIcon
        theme="AIRASIA_CLASSIC"
        type={topNotification.type}
      />
    </PhoenixTheme>
  );

  const onHistorySearch = useCallback(() => {
    const pushState = {
      navigationFrom: 'roomSelect',
    };
    history.push(constants.ROUTE.HOTEL.HOTEL_LIST, pushState);
  }, [history]);

  const searchHandler = useCallback(
    async listingParams => {
      await updateHotelFilter(listingParams);
      handleSoldOutSearchCriteria(listingParams, initialSearchedFilter);
      setViewGuestReviews(true);
      if (listingParams.locationId !== filter.locationId) {
        setTimeout(() => {
          if (listingParams.location === hotel?.name) {
            fetchHotelDetail(hotelId);
            onCallFetchHotelRooms();
          } else {
            onHistorySearch();
          }
        }, 500);
      } else {
        setTimeout(() => {
          if (listingParams.location !== hotel?.name) {
            onHistorySearch();
          } else {
            fetchHotelDetail(hotelId);
            onCallFetchHotelRooms();
          }
        }, 500);
      }
    },
    [
      fetchHotelDetail,
      updateHotelFilter,
      onHistorySearch,
      filter.locationId,
      onCallFetchHotelRooms,
      hotel,
      initialSearchedFilter,
    ]
  );

  const closeMapModal = useCallback(() => {
    setIsMapModalOpen(false);
  }, []);

  let showBigPayCard = false;
  const bigPayCardLabel = translate('common.hotelBigpayCardPromotion');
  if (bigPayCardLabel !== 'common.hotelBigpayCardPromotion') {
    showBigPayCard = true;
  }

  const breadcrumbsNavigations = [
    {
      label: translate('breadcrumbs.home'),
      link: constants.ROUTE.ROOT,
    },
  ];
  if (filter.location) {
    breadcrumbsNavigations.push({
      label: translate('breadcrumbs.hotelList', {
        location: filter.location,
      }),
      link: `${constants.ROUTE.HOTEL.HOTEL_LIST}${location.search}`,
    });
  }
  breadcrumbsNavigations.push({ label: hotelName });

  const { supplier_id: supplierId, aaid } = debug || {};
  const text = supplierId && aaid;

  const [isCalendarOpenedByDeepLink, setIsCalendarOpenedByDeepLink] = useState(
    false
  );

  useEffect(() => {
    if (hotelId && location.search === '' && !isFailed && !isFetching) {
      setIsCalendarOpenedByDeepLink(true);
    }
  }, [hotelId, location.search, isFailed, isFetching]);

  useEffect(() => {
    setHotelDetailState(hotelDetailState => ({
      ...hotelDetailState,
      rooms,
    }));
    setFilteredRooms(rooms);
  }, [rooms]);

  useEffect(() => {
    if (hotelDetail.id && !isFailed && !isFetching) {
      setTimeout(() => {
        setIsShowFallBackMessage(false);
      }, 4000);
    }
  }, [isShowFallBackMessage, hotelDetail.id, isFailed, isFetching]);

  const onReviewsClick = () => {};

  const tripAdvisorMounted = rating => {
    return;
  };

  const hotelDescription = useRef(null);
  const [clickShowMore, setClickShowMore] = useState(false);

  const toggleClickShowMore = useCallback(value => setClickShowMore(value), []);

  const { city = '', country = '' } = address;

  const shortAddress = [city, country].join(', ');

  const showPointsOfInterest = true;

  const closePOIModal = useCallback(() => {
    setIsPOIModalOpen(false);
  }, []);

  const isFromWMP = useMemo(() => {
    return isFromWechat(filter.channel);
  }, [filter.channel]);

  useEffect(() => {
    const mapProps = {
      title: translate('hotelDetailsPage.map.title'),
      seeOnMapLabel: translate('hotelDetailsPage.map.seeOnAMap'),
      onClick: () => {
        trackHotelSeeOnAMapWeb();
        setIsMapModalOpen(true);
      },
      address: isMobileView ? shortAddress : locationText,
    };

    const poiProps = Array.isArray(pointsOfInterest) &&
      pointsOfInterest.length > 0 && {
        title: translate('hotelDetailsPage.poi.title'),
        pointsOfInterest: pointsOfInterest,
        seeAllPlacesLabel: translate('hotelDetailsPage.poi.seeAllPlaces'),
        onClickShowMore: () => {
          setIsPOIModalOpen(true);
        },
      };

    setRoomMapWithPOIProps(prevProps => {
      return {
        ...prevProps,
        mapProps,
        poiProps,
      };
    });
  }, [
    isMobileView,
    locationText,
    shortAddress,
    showPointsOfInterest,
    pointsOfInterest,
    trackHotelSeeOnAMapWeb,
  ]);
  useEffect(() => {
    if (isEmptyHotelRoom) {
      errorPopOutHandler();
    }
  }, [isEmptyHotelRoom]);
  const errorPopOutHandler = () => {
    const objGtm = {
      event: 'trackEventNoEcommerce',
      'eventDetails.category': 'Hotels SA|Room Select heatmap',
      'eventDetails.action': `view`,
      'eventDetails.label': `no hotels`,
    };
    trackErrorPopOutRoom(objGtm);
  };
  const roomFilterUse = filterName => {
    trackHotelRoomFilterUse({ filterName: filterName.text });
  };
  const isCertifiedSuper = useMemo(() => {
    return rooms?.[0]?.rates?.[0]?.discountData?.type === 'certified_super'
      ? true
      : false;
  }, [rooms]);

  const showEmptyHotelRoom = useMemo(() => {
    return isEmptyHotelRoom || rooms?.length === 0;
  }, [rooms, isEmptyHotelRoom]);

  const MemoizedLowInventoryFomoBanner = useMemo(() => {
    // eslint-disable-next-line react/prop-types
    const LowInventoryFomoBanner = ({ hotelID = '' }) => {
      const msg = LowInventoryFOMOmsg({
        isBanner: true,
        featureEnableFOMO: enableFomoMessagesWeb,
        bookingDoneFomo: configBookingDoneFomo,
        fastFillingFomo: configFastFillingFomo,
        hotelID: hotelID,
      });

      if (msg.trim() === '') {
        // Return null if lowInventoryFomoBanner is an empty string
        return null;
      }
      return (
        <>
          {!isEmptyHotelRoom && (
            <Wrapper marginTop={1} marginBottom={25}>
              <PhoenixTheme theme={appEnabledTheme}>
                <NotificationBannerInline
                  message={msg}
                  type="fomoWarning"
                  fullWidth
                />
              </PhoenixTheme>
            </Wrapper>
          )}
        </>
      );
    };

    return LowInventoryFomoBanner;
  }, [
    isEmptyHotelRoom,
    enableFomoMessagesWeb,
    configBookingDoneFomo,
    configFastFillingFomo,
  ]);

  const appEnabledTheme = enableRebranding ? 'AIRASIA_MOVE' : 'AIRASIA_CLASSIC';

  return (
    <PhoenixTheme theme={appEnabledTheme}>
      <PreCheckout
        locale={filter.languageId}
        roomSelected={!isEmpty(navigationObject)}
        selectedRate={hotelDetail?.selectedRate}
        preCheckout={preCheckout}
        isEnabled={enablePreCheckoutCall}
        redirect={(priceChanged = false) => {
          const { newURL, cancelPenalties, hotelURL, soldOutSearchCriteria } =
            navigationObject || {};
          let navURL = newURL;

          if (priceChanged) {
            const separator = navURL.includes('?') ? '&' : '?';
            navURL = `${navURL}${separator}priceChanged=true`;
          }

          history.push(navURL, {
            cancelPenalties,
            hotelURL,
            soldOutSearchCriteria,
          });
        }}
      >
        {helmet}
        <Wrapper paddingTop={!isFromWMP ? [56, 0] : [0]}>
          {!isFromWMP && (
            <HotelsSummaryHeaderWrapper
              isMobileView={isMobileView}
              theme={appEnabledTheme}
            >
              {filter.isInit && id && (
                <HotelsSummaryHeader
                  move={enableRebranding}
                  theme={appEnabledTheme}
                  showSearchHeader={true}
                  filter={filter}
                  enableMobileSearchWidget
                  enableDesktopSearchWidget
                  handleMobileBackButton={() => {
                    navigateBack();
                    trackMobileBackButton(BACK_FROM_GUEST_DETAIL_LABEL);
                  }}
                  updateFilter={searchHandler}
                  destination={filter.locationId}
                  isCalendarOpenedByDeepLink={isCalendarOpenedByDeepLink}
                  trackHotelSearchWidget={trackHotelSearchWidget}
                  isApplyHotelList
                  uhswData={uhswData}
                  suggestion={{
                    name: hotelName,
                  }}
                />
              )}
            </HotelsSummaryHeaderWrapper>
          )}
        </Wrapper>
        <Container>
          {/* hide the main view when image gallery flag is enabled and gallery
          is showing so that the app will show only 1 scroll bar */}
          {(!hasImageGallery || !galleryVisible) && (
            <Wrapper paddingBottom="42px" width="100%">
              <LoadingWrapper
                fetching={!id || isFetching}
                failed={isFailed}
                theme={appEnabledTheme}
                move={enableRebranding}
              >
                {topMsg && (
                  <Row display={['none', 'flex']} marginTop={5}>
                    {topMsg}
                  </Row>
                )}
                <Wrapper display={['none', 'block']} marginY={5}>
                  <Breadcrumbs navigations={breadcrumbsNavigations} />
                </Wrapper>
                <Row
                  id="hotel-details-mobile"
                  display={['flex', 'none']}
                  flexDirection="column"
                  marginBottom="26px"
                >
                  <HeroImageContainer
                    onClick={() => {
                      setGalleryVisible(true);
                      setGalleryRoomDetail(null);
                    }}
                  >
                    {isCertifiedSuper && (
                      <CertifiedSuper
                        isMobileView={isMobileView}
                        title={translate(
                          'hotelCouponDiscount.certified_super.listing.Description'
                        )}
                      >
                        <Image
                          src={
                            'https://storage.googleapis.com/static.airasia.com/design/logos/super-certified.svg'
                          }
                          width="48px"
                          height="48px"
                          id="main-picture-super-certified"
                          alt="Certified Super"
                          fallbackContent={
                            <HotelGenericImage
                              width="48px"
                              height="48px"
                              id="main-picture-super-certified"
                            />
                          }
                        />
                      </CertifiedSuper>
                    )}
                    {hasImageGallery && (
                      <ImageCount right="0">
                        <CollectionsIcon /> + {gallery.length - 1}
                      </ImageCount>
                    )}
                    <Image
                      src={image}
                      width="100vw"
                      height="60vw"
                      position="relative"
                      left="-16px"
                      id="main-picture-mobile"
                      alt={hotelName}
                      fallbackContent={
                        <HotelGenericImage
                          width="100vw"
                          maxWidth="100vw"
                          height="60vw"
                          position="relative"
                          left="-16px"
                          id="main-picture-mobile"
                        />
                      }
                    />
                  </HeroImageContainer>
                  {topMsg && (
                    <Row display={['flex', 'none']} marginTop={4}>
                      {topMsg}
                    </Row>
                  )}
                  <Wrapper marginLeft={0} mt={4}>
                    <H3
                      id="hotel-name-mobile"
                      lineHeight="31px"
                      numberOfLines="2"
                    >
                      {hotelName}
                      {text && <Debug>{`(${supplierId}-${aaid})`}</Debug>}
                    </H3>
                    <StarRating rating={Number(starRating)} />
                    {locationText && (
                      <>
                        <Location
                          id="location-mobile"
                          width="100%"
                          location={locationText}
                          numberOfLines={2}
                        >
                          {hotel && (
                            <ShowMapView
                              theme={appEnabledTheme}
                              move={enableRebranding}
                              onClick={() => {
                                trackHotelShowMapWeb();
                                setIsMapModalOpen(true);
                              }}
                            >
                              {translate('hotelDetailsPage.map.showMap')}
                            </ShowMapView>
                          )}
                        </Location>
                      </>
                    )}
                    {tripAdvisorData ? (
                      <>
                        <Divider
                          color="#d9dbe0"
                          style={{
                            position: 'relative',
                            width: '100vw',
                            left: '50%',
                            right: '50%',
                            marginLeft: '-50vw',
                            marginRight: '-50vw',
                          }}
                        />
                        <TripAdvisor
                          isRoomSelection
                          {...tripAdvisorData}
                          onReviewsClick={onReviewsClick}
                          tripAdvisorMounted={tripAdvisorMounted}
                        />
                        <Divider
                          color="#d9dbe0"
                          style={{
                            position: 'relative',
                            width: '100vw',
                            left: '50%',
                            right: '50%',
                            marginLeft: '-50vw',
                            marginRight: '-50vw',
                          }}
                        />
                      </>
                    ) : (
                      <UserReviewOverview
                        {...guestReviewOverviewProps}
                        theme={appEnabledTheme}
                        move={enableRebranding}
                      />
                    )}
                  </Wrapper>
                  <DescriptionText id="descriptions-mobile">
                    {shortDescription}
                  </DescriptionText>
                  {amenities && amenities.length > 0 && (
                    <Wrapper id="amenities-mobile" mt={4}>
                      <AmenitiesList
                        theme={appEnabledTheme}
                        move={enableRebranding}
                        amenities={amenities}
                        isShowPopover
                      />
                    </Wrapper>
                  )}

                  {isMobileView && (
                    <RoomMapWithPOI
                      {...roomMapWithPOIProps}
                      isMobileView={isMobileView}
                      theme={appEnabledTheme}
                      move={enableRebranding}
                    />
                  )}
                </Row>
                <Row
                  id="hotel-details"
                  marginBottom="10px"
                  display={['none', 'grid']}
                  flexDirection="column"
                  gridTemplateColumns="auto 1fr"
                >
                  <HeroImageContainer
                    onClick={() => {
                      setGalleryVisible(true);
                      setGalleryRoomDetail(null);
                    }}
                  >
                    {isCertifiedSuper && (
                      <CertifiedSuper
                        isMobileView={isMobileView}
                        title={translate(
                          'hotelCouponDiscount.certified_super.listing.Description'
                        )}
                      >
                        <Image
                          src={
                            'https://storage.googleapis.com/static.airasia.com/design/logos/super-certified.svg'
                          }
                          width="48px"
                          height="48px"
                          id="main-picture-super-certified"
                          fallbackContent={
                            <HotelGenericImage
                              width="48px"
                              height="48px"
                              id="main-picture-super-certified"
                            />
                          }
                        />
                      </CertifiedSuper>
                    )}
                    {hasImageGallery && (
                      <ImageCount right="16px">
                        <CollectionsIcon /> + {gallery.length - 1}
                      </ImageCount>
                    )}
                    <Image
                      src={image}
                      width="820px"
                      height="500px"
                      borderRadius="4px"
                      id="main-picture"
                      alt={hotelName}
                      fallbackContent={
                        <HotelGenericImage
                          width="820px"
                          height="500px"
                          borderRadius="4px"
                          id="main-picture"
                        />
                      }
                    />
                  </HeroImageContainer>
                  <Wrapper marginLeft="18px">
                    <H2
                      id="hotel-name"
                      lineHeight="31px"
                      marginBottom="8px"
                      numberOfLines="2"
                    >
                      {hotelName}
                      {text && <Debug>{`(${supplierId}-${aaid})`}</Debug>}
                    </H2>
                    <StarRating rating={starRating} />
                    {locationText && (
                      <>
                        <Location
                          id="location"
                          width="100%"
                          location={locationText}
                          numberOfLines={2}
                        >
                          {hotel && (
                            <ShowMapView
                              theme={appEnabledTheme}
                              move={enableRebranding}
                              onClick={() => {
                                trackHotelShowMapWeb();
                                setIsMapModalOpen(true);
                              }}
                            >
                              {translate('hotelDetailsPage.map.showMap')}
                            </ShowMapView>
                          )}
                        </Location>
                      </>
                    )}
                    {tripAdvisorData ? (
                      <>
                        <Divider />
                        <TripAdvisor
                          isRoomSelection
                          {...tripAdvisorData}
                          onReviewsClick={onReviewsClick}
                          tripAdvisorMounted={tripAdvisorMounted}
                        />
                      </>
                    ) : (
                      <UserReviewOverview
                        {...guestReviewOverviewProps}
                        theme={appEnabledTheme}
                        move={enableRebranding}
                      />
                    )}
                    <DescriptionText id="descriptions">
                      {shortDescription}
                    </DescriptionText>
                    {amenities && (
                      <Wrapper>
                        <Divider marginTop={tripAdvisorData ? 0 : 4} />
                        <SubBodyText fontWeight={500}>
                          {translate('hotelDetailsPage.hotelAmenities')}
                        </SubBodyText>
                        <AmenitiesList
                          theme={appEnabledTheme}
                          move={enableRebranding}
                          amenities={amenities}
                          type={AmenitiesType.TWO_COLUMN}
                          size={20}
                          isShowPopover={amenities.length >= 6 ? true : false}
                          allHotelAmenitiesLabel={translate(
                            'hotelDetailsPage.amenity.all'
                          )}
                          moreLabel={translate(
                            'hotelDetailsPage.amenity.more',
                            {
                              amount:
                                amenities.length >= 6
                                  ? amenities.length - 5
                                  : amenities.length,
                            }
                          )}
                        />
                      </Wrapper>
                    )}
                  </Wrapper>
                </Row>

                {!isMobileView && (
                  <Wrapper marginBottom="24px" marginTop="24px">
                    <RoomMapWithPOI
                      {...roomMapWithPOIProps}
                      isMobileView={isMobileView}
                      theme={appEnabledTheme}
                      move={enableRebranding}
                    />
                  </Wrapper>
                )}

                {bigMembership && (
                  <>
                    <Row>
                      {isMobileView && (
                        <Divider
                          style={{
                            position: 'absolute',
                            width: '100%',
                            left: '0px',
                          }}
                        />
                      )}
                    </Row>
                    <Wrapper style={{ position: 'relative' }}>
                      {
                        <HotelSwitch
                          bigPoints={bigMembership}
                          isLoggedIn={isLoggedUser(credential)}
                          isMobileView={isMobileView}
                          filter={filter}
                          updateFilter={updateBigPointFilter}
                        />
                      }
                    </Wrapper>
                  </>
                )}
                <Wrapper>
                  <Wrapper
                    width={isMobileView ? [1, 3 / 4] : '100%'}
                    position={'relative'}
                  >
                    {isLoggedUser(credential) && displayBigPointSuccessCard && (
                      <BigPointsSuccessCard isMobileView={isMobileView}>
                        {translate('common.bigPoints.redeem.success')}
                      </BigPointsSuccessCard>
                    )}
                  </Wrapper>
                  {showVoucherBanner && enableVoucherBanner ? (
                    <Wrapper marginBottom={16}>
                      <PhoenixTheme theme={appEnabledTheme}>
                        <VoucherBannerContainer isMobileView={isMobileView}>
                          <Row>
                            <VoucherImage isMobileView={isMobileView}>
                              <Image
                                src={
                                  'https://storage.googleapis.com/static.airasia.com/design/images/flight/banner-bgd-voucher.jpg'
                                }
                                width={isMobileView ? '127%' : '100%'}
                                height={isMobileView ? '95%' : '64%'}
                                objectFit={''}
                              />
                            </VoucherImage>
                            <VoucherMessage isMobileView={isMobileView}>
                              {translate(`voucherMessage.${filter.currency}`)}
                            </VoucherMessage>
                          </Row>
                        </VoucherBannerContainer>
                      </PhoenixTheme>
                    </Wrapper>
                  ) : (
                    showBigPayCard && (
                      <Wrapper
                        marginTop={
                          isLoggedUser(credential) && !isMobileView ? 36 : 0
                        }
                        marginBottom={16}
                      >
                        <PhoenixTheme theme={appEnabledTheme}>
                          <NotificationBannerInline
                            message={bigPayCardLabel}
                            fullWidth
                          />
                        </PhoenixTheme>
                      </Wrapper>
                    )
                  )}
                </Wrapper>
                {showRoomFilters && (
                  <Wrapper>
                    <PhoenixTheme>
                      <RoomFilters
                        data={roomFilterItems}
                        selectionHandler={roomFiltersSelectionHandler}
                        translations={{
                          title: translate('roomFilters.filterBy'),
                          reset: translate('roomFilters.reset'),
                          showMoreText: translate('roomFilters.showMore'),
                        }}
                        move={enableRebranding}
                      />
                    </PhoenixTheme>
                  </Wrapper>
                )}
                {showSoldOutMessage && (
                  <Wrapper marginTop={36} marginBottom={16}>
                    <PhoenixTheme theme={appEnabledTheme}>
                      <NotificationBannerInline
                        message={translate('hotelDetails.soldOutSnackbar')}
                        type="newInfo"
                        fullWidth
                      />
                    </PhoenixTheme>
                  </Wrapper>
                )}
                <MemoizedLowInventoryFomoBanner hotelID={hotelId} />
                <div ref={roomListRef}>
                  {filteredRooms &&
                    filteredRooms.length > 0 &&
                    filteredRooms.map((room, index) => {
                      const rates = room.rates.map(rate => {
                        const {
                          rateNameId,
                          baseRate,
                          strikethroughPrice,
                          refundType,
                          isPayLater,
                          payLaterDate,
                          isCrossSellRate,
                          isRecommended,
                          isPayAtHotel,
                          totalDiscountedPrice,
                          isDutyTravelRate,
                        } = rate;
                        const [
                          firstPenalty = {
                            penalty_type: CANCELLATION_PENALTY_TYPES.FULL,
                          },
                        ] = rate.cancelPenalties;
                        let { end: firstPenaltyEndDate } = firstPenalty;
                        const timePlus = ' 23:59:00';
                        if (
                          firstPenaltyEndDate &&
                          firstPenaltyEndDate.length === 10
                        ) {
                          //cancel penalties of Agoda always return yyyy-mm-dd
                          firstPenaltyEndDate = firstPenaltyEndDate + timePlus;
                        } else if (
                          firstPenaltyEndDate &&
                          firstPenaltyEndDate.length === 16
                        ) {
                          const firstPenaltyEndDateSplit = firstPenaltyEndDate.split(
                            ' '
                          );
                          //cancel penalties  of GoQuo always return yyyy-mm-dd hh:mm
                          firstPenaltyEndDate =
                            firstPenaltyEndDateSplit[0] + timePlus;
                        } else {
                          firstPenaltyEndDate = firstPenaltyEndDate?.split(
                            '+'
                          )[0];
                        }
                        let isNonRefundable = false;
                        let isFreeCancellation = false;
                        switch (refundType) {
                          case CANCELLATION_REFUND_TYPE.FULL_REFUND:
                            isFreeCancellation = true;
                            break;
                          case CANCELLATION_REFUND_TYPE.PARTIAL_REFUND:
                            isFreeCancellation = true;
                            break;
                          case CANCELLATION_REFUND_TYPE.NO_REFUND:
                            isNonRefundable = true;
                            break;
                          default:
                            isNonRefundable = false;
                            isFreeCancellation = false;
                            break;
                        }
                        const penaltyDescriptionLabel = !isMobileView
                          ? translate(
                              `roomTags.cancellationPolicyText.${refundType}`,
                              firstPenaltyEndDate && {
                                date: formatLongerLocalTime(
                                  firstPenaltyEndDate
                                ),
                              }
                            )
                          : translate(
                              `roomTags.cancellationPolicyText.${refundType}.Mobile`,
                              firstPenaltyEndDate && {
                                date: formatLongerLocalTime(
                                  firstPenaltyEndDate
                                ),
                              }
                            );
                        const isShowBogoPromo =
                          isEnableHotelBogoPromo &&
                          BogoPromoDiscountId.includes(rate?.discountData?.id);
                        const isRM9PromoEnabled =
                          enableRM9Button &&
                          BogoPromoDiscountId.includes(rate?.discountData?.id); //Need to change this to new rate object
                        const bogoMessage =
                          !isMobileView &&
                          isShowBogoPromo &&
                          BogoPromoDiscountId.includes(rate?.discountData?.id)
                            ? enableRM9Button && rate?.second_night
                              ? translate(`common.rm9.promoMessage`, {
                                  amount:
                                    `${rate?.second_night?.tag?.currency}${rate?.second_night?.tag?.value}` ||
                                    'RM9',
                                })
                              : translate(
                                  `hotelCouponDiscount.${rate?.discountData?.id}.listing.Description`
                                )
                            : null;
                        const bogoTag =
                          isShowBogoPromo &&
                          BogoPromoDiscountId.includes(rate?.discountData?.id)
                            ? enableRM9Button && rate?.second_night
                              ? translate(
                                  `hotelCouponDiscount.RM9.listing.tag`,
                                  {
                                    promo:
                                      `${rate?.second_night?.tag?.currency}${rate?.second_night?.tag?.value}` ||
                                      'RM9',
                                  }
                                )
                              : translate(
                                  `hotelCouponDiscount.${rate?.discountData?.id}.listing.title`
                                )
                            : null;
                        return {
                          ...rate,
                          isRM9PromoEnabled: isRM9PromoEnabled,
                          isBogoEnabled: isShowBogoPromo,
                          bogoMessage: bogoMessage,
                          bogoTag: bogoTag,
                          rateName: `${translate(
                            'roomCard.rateName.name_' + rateNameId
                          )}`,
                          bigPoint: rate.big_life?.points_earn
                            ? {
                                template:
                                  Number(rate.big_life?.multiplier || 0) > 1
                                    ? translate(
                                        'common.earnBigPointTemplateForLoggedIn'
                                      )
                                    : translate(
                                        'common.earnBigPointTemplateForLoggedIn',
                                        {
                                          multiplier_template: '',
                                          point_template: numberFormat(
                                            rate.big_life.points_earn
                                          ),
                                        }
                                      ),
                                pointTemplate: translate(
                                  'common.bigPointValueTemplate'
                                ),
                                pointValue: numberFormat(
                                  rate.big_life.points_earn
                                ),
                                multiplierTemplate: translate(
                                  'common.bigPointMultiplierTemplate'
                                ),
                                multiplierValue: Number(
                                  rate.big_life.multiplier
                                ),
                              }
                            : null,
                          freeCancellationText:
                            isFreeCancellation &&
                            refundType === CANCELLATION_REFUND_TYPE.FULL_REFUND
                              ? penaltyDescriptionLabel
                              : null,
                          partiallyRefundableText:
                            isFreeCancellation &&
                            refundType ===
                              CANCELLATION_REFUND_TYPE.PARTIAL_REFUND
                              ? penaltyDescriptionLabel
                              : null,
                          nonRefundableText: isNonRefundable
                            ? penaltyDescriptionLabel
                            : null,
                          payLaterText:
                            isPayLater && payLaterDate
                              ? isMobileView
                                ? translate(
                                    `tripDetails.payLater.payNothingUntil`,
                                    {
                                      date: formatPayLaterDate(
                                        payLaterDate,
                                        PAY_LATER_FORMAT_TIME
                                      ),
                                    }
                                  )
                                : translate(
                                    `tripDetails.payLater.payNothingUntil`,
                                    {
                                      date: formatPayLaterDate(
                                        payLaterDate,
                                        PAY_LATER_FORMAT_SHORT_TIME
                                      ),
                                    }
                                  )
                              : null,
                          payAtHotelText: isPayAtHotel
                            ? translate('tags.pay_at_hotel_description')
                            : null,
                          payAtHotel: isPayAtHotel
                            ? translate('tags.pay_at_hotel')
                            : null,
                          recommendedText: isDutyTravelRate
                            ? translate('common.duty_travel_rate')
                            : isRecommended
                            ? 'Recommended'
                            : null,
                          flierDiscountText:
                            isCrossSellRate && isLoggedUser(credential)
                              ? translate('tags.flyer_discount')
                              : null,
                          CTAButtonText:
                            isDutyTravelRate || isPayAtHotel || isPayLater
                              ? translate('roomCard.reserveNow')
                              : translate('roomCard.bookNow'),
                          discountText: discountText(
                            baseRate.value,
                            strikethroughPrice ? strikethroughPrice.value : 0,
                            filter.languageId
                          ),
                          strikeThrough: strikethroughPrice,
                          payLater: isPayLater
                            ? translate('roomCard.buyNowPayLater')
                            : null,
                          isFreeCancellation: rate.isFreeCancellation,
                          isNonRefundable: rate.isNonRefundable,
                          isPayAtHotel: rate.isPayAtHotel,
                          refundType: refundType,
                          price: baseRate,
                          totalPrice: {
                            ...totalDiscountedPrice,
                            text: translate(
                              'bookingDetail.paymentDetails.payLater.total'
                            ),
                          },
                          cancellationPenaltyType: refundType,
                        };
                      });

                      const rateDiscounts = room.rates.map(rate => {
                        const isDiscountExist =
                          rate.discountData !== undefined &&
                          !BogoPromoDiscountId.includes(rate.discountData.id);

                        return {
                          id: rate.id,
                          title: isDiscountExist
                            ? translate(
                                `hotelCouponDiscount.${rate.discountData.type}.listing.title`
                              )
                            : '',
                          type: isDiscountExist ? rate.discountData.type : '',
                        };
                      });

                      const bigPointsFilter = filter
                        ? {
                            bigMembership: bigMembership,
                            applyBigpoints: applyBigpoints,
                          }
                        : {};

                      const totalNight = countNights(
                        filter?.checkin,
                        filter?.checkout
                      );

                      const {
                        squareFeetArea,
                        squareMeterArea,
                        roomOccupancy,
                        otherAmenities,
                        roomAmenities,
                      } = room || {};

                      let areaAmenityName = '';

                      if (squareFeetArea && squareMeterArea) {
                        areaAmenityName = translate('roomCard.areaAmenity', {
                          squareMeterArea,
                          squareFeetArea,
                        });
                      } else if (squareMeterArea > 0) {
                        areaAmenityName = translate('roomCard.squareMeter', {
                          squareMeterArea,
                        });
                      } else if (squareFeetArea > 0) {
                        areaAmenityName = translate('roomCard.squareFeet', {
                          squareFeetArea,
                        });
                      }
                      const occupancyName =
                        roomOccupancy?.max_occupancy >= 1
                          ? translate('roomCard.roomOccupancyMultiple', {
                              occupancy: roomOccupancy?.max_occupancy,
                            })
                          : '';
                      const {
                        extraRoomAmenities,
                      } = createExtraRoomLevelAmenitiesMobile({
                        areaAmenityName,
                        occupancyName,
                        otherAmenities,
                        roomAmenities,
                      });
                      const amenities = isExtraRoomAmenitiesEnabled
                        ? extraRoomAmenities
                        : roomAmenities;

                      return (
                        <LazyLoad
                          key={index}
                          offset={0}
                          placeholder={
                            <ShimmerHotelCard isMobileView={isMobileView} />
                          }
                        >
                          <Wrapper
                            id={room.id}
                            className="room"
                            marginBottom={4}
                          >
                            <PhoenixTheme theme={appEnabledTheme}>
                              <NewRoomCard
                                theme={appEnabledTheme}
                                {...room}
                                locale={filter.languageId}
                                bigPointsFilter={bigPointsFilter}
                                heroImage={room.image}
                                onLoadImage={() =>
                                  trackContentDownloadCompleted({
                                    url: room.image,
                                  })
                                }
                                rateDiscounts={rateDiscounts}
                                images={room.galleryV2}
                                amenities={amenities}
                                isImageGalleryEnabled={
                                  room.galleryV2.length >= 1
                                }
                                rates={rates}
                                isShowRateName
                                occupancy={
                                  filter.guests
                                    ? {
                                        adult: filter.guests.adults,
                                        child: filter.guests.children.length,
                                      }
                                    : {}
                                }
                                address={address}
                                isSingleOption={room.rates.length === 1}
                                isMobile={isMobileView}
                                translations={{
                                  seeMore: translate(
                                    'hotelDetailsPage.guestReview.showMore'
                                  ),
                                  perNight: translate('roomCard.night'),
                                  photo: translate('roomCard.photos'),
                                  morePhoto: translate('roomCard.morePhotos'),
                                  optionsHeader: translate(
                                    'roomCard.optionsAndBenefits'
                                  ),
                                  includedHeader: translate(
                                    'roomCard.includedInPrice'
                                  ),
                                  showMore: translate('roomCard.showMore'),
                                  sleepsHeader: translate('roomCard.sleeps'),
                                  priceHeader: translate(
                                    'roomCard.pricePerRoomPerNight'
                                  ),
                                  freeCancellation: translate(
                                    'tags.free_cancellation'
                                  ),
                                  payLater: translate(
                                    'roomCard.buyNowPayLater'
                                  ),
                                  nonRefundable: translate(
                                    'hotelDetailsConfirm.hotelBookingInfo.nonRefundable'
                                  ),
                                  partiallyRefundable: translate(
                                    'cancellationPolicy.Partial'
                                  ),
                                  showMoreOptions: translate(
                                    'roomCard.seeRateMoreOptions'
                                  ),
                                  showLessOptions: translate(
                                    'roomCard.seeLessOptions'
                                  ),
                                  seeAllAmenities: translate(
                                    'roomCard.seeAllAmenities'
                                  ),
                                  moreDetails: translate(
                                    'roomCard.moreDetails'
                                  ),
                                  benefitsIncluded: translate(
                                    'roomCard.benefitsIncludedInThePrice'
                                  ),
                                  option: translate('roomCard.option'),
                                  perRoomPerNight: translate(
                                    'hotelCard.priceText'
                                  ),
                                  adults: translate('roomCard.adults'),
                                  children: translate('roomCard.children'),
                                  allHotelAmenities: translate(
                                    'hotelDetailsPage.amenity.all'
                                  ),
                                  all: translate('common.all'),
                                  aboutTheRoom: translate(
                                    'roomCard.aboutTheRoom'
                                  ),
                                  at: translate('roomCard.at'),
                                  viewRoomImages: translate(
                                    'roomCard.viewRoomImages'
                                  ),
                                  amenities: translate('common.amenities'),
                                  ratePlanDetails: translate(
                                    'roomCard.ratePlanDetails'
                                  ),
                                  total: translate(
                                    'bookingDetail.paymentDetails.payLater.total'
                                  ),
                                  messageFOMO: translate(
                                    'hotelDetails.soldOutFomo'
                                  ),
                                  soldOutCTA: translate('hotel.soldOut'),
                                }}
                                onSelectRoom={onRoomSelect}
                                soldOutBedGroupTokens={
                                  soldOutTokens ||
                                  extractTokens(
                                    soldOutSearchCriteria,
                                    hotelDetail
                                  )
                                }
                                lowInventoryFOMO={LowInventoryFOMOmsg({
                                  rooms: room,
                                  leadRoomOnly: true,
                                  leadRateOnly: true,
                                  featureEnableFOMO: enableFomoMessagesWeb,
                                  hotelsLastRoom: configLastRoomFomo,
                                  hotelsPriceDrop: configPriceDropFomo,
                                  hotelID: hotelId,
                                  lastRoomCount: configLastRoomCountFomo,
                                  roomSource: rooms,
                                })}
                                payNowMessage={translate(
                                  'common.payNowFilter.message',
                                  {
                                    percent: translate('payNowFilter.percent'),
                                    promoCode: translate(
                                      'payNowFilter.promoCode'
                                    ),
                                  }
                                )}
                                isPayNowEnabled={isPayNowMessageEnabled}
                                isBlockedRateIsEnabled={isBlockedRateIsEnabled}
                                getTranslatedPricebreakdownData={
                                  getRM9PriceBreakdownData
                                }
                                move={enableRebranding}
                                isExtraRoomAmenitiesEnabled={
                                  isExtraRoomAmenitiesEnabled
                                }
                              />
                            </PhoenixTheme>
                          </Wrapper>
                        </LazyLoad>
                      );
                    })}
                </div>
                {showEmptyHotelRoom && (
                  <EmptyStateWrapper
                    isEmpty={true}
                    type="empty_hotel_room_list"
                    isMobileView={isMobileView}
                    theme={appEnabledTheme}
                    move={enableRebranding}
                  />
                )}
                <div
                  className="guest-reviews"
                  id="reviews_guest"
                  ref={guestReview}
                >
                  {guestReviewIsFailedAndIsFetching ? (
                    !isMobileView ? (
                      <div id="hotel-guest-review">
                        <Divider />
                        <H3
                          id="hotel-guest-review-title"
                          lineHeight="26px"
                          marginBottom="2px"
                          marginTop="32px"
                          fontSize={['16px', '20px']}
                        >
                          {translate('hotelDetailsPage.guestReview.title')}
                        </H3>
                        <Wrapper>
                          <Row>
                            <Wrapper width={'350px'}>
                              <UserReviewsRating
                                theme={appEnabledTheme}
                                move={enableRebranding}
                                {...userReviewRatingProps}
                              />
                            </Wrapper>
                            <Column width={'100%'}>
                              <GuestReviews
                                theme={appEnabledTheme}
                                move={enableRebranding}
                                elements={guestReviews?.reviews}
                                hotelId={hotelId}
                                isMobile={isMobileView}
                                trackHotelDetailedReviewPagination={
                                  trackHotelDetailedReviewPagination
                                }
                              />
                            </Column>
                          </Row>
                        </Wrapper>
                        <Divider />
                      </div>
                    ) : (
                      <div id="hotel-guest-review">
                        <Divider />
                        <H3
                          id="hotel-guest-review-title"
                          lineHeight="26px"
                          marginBottom="2px"
                          marginTop="32px"
                          fontSize={['16px', '20px']}
                        >
                          {translate('hotelDetailsPage.guestReview.title')}
                        </H3>
                        <Wrapper>
                          <Column>
                            <Wrapper>
                              <UserReviewsRating
                                theme={appEnabledTheme}
                                move={enableRebranding}
                                {...userReviewRatingProps}
                              />
                              <Divider />
                            </Wrapper>
                            <Column>
                              <GuestReviews
                                theme={appEnabledTheme}
                                move={enableRebranding}
                                elements={guestReviews.reviews}
                                hotelId={hotelId}
                                isMobile={isMobileView}
                              />
                            </Column>
                          </Column>
                        </Wrapper>
                        <Divider />
                      </div>
                    )
                  ) : (
                    <></>
                  )}
                </div>
                {!isEmptyHotelRoom && (
                  <div ref={hotelDescription}>
                    <DescriptionAndPolicies
                      them={appEnabledTheme}
                      move={enableRebranding}
                      clickShowMore={clickShowMore}
                      toggleClickShowMore={toggleClickShowMore}
                      description={description}
                      checkinDetails={checkinDetails}
                      checkoutDetails={checkoutDetails}
                      policies={policies}
                      fees={fees}
                      onDescriptionExpand={() => {
                        trackHotelShowMoreDescriptionClicked(hotelDetail);
                      }}
                      onPolicyExpand={() => {
                        trackHotelShowMorePolicyClicked(hotelDetail);
                      }}
                    />
                  </div>
                )}
              </LoadingWrapper>
              {isFailed && (
                <EmptyStateWrapper
                  isEmpty={true}
                  action={navigateBack}
                  type={!location.state ? 'empty_browser_history' : null}
                  theme={appEnabledTheme}
                  move={enableRebranding}
                />
              )}
            </Wrapper>
          )}
          {galleryVisible && hasImageGallery && (
            <Modal
              modalIsOpen={true}
              onClose={() => {}}
              contentOverflow="hidden"
            >
              <>
                <PhoenixTheme theme={appEnabledTheme}>
                  {isMobileView ? (
                    <GalleryV2MobileContainer>
                      <ImageGalleryMobile
                        images={
                          !galleryRoomDetail
                            ? galleryV2
                            : galleryRoomDetail.gallery
                        }
                        hotelDetail={{
                          name: !galleryRoomDetail
                            ? hotelName
                            : galleryRoomDetail.name,
                          address,
                        }}
                        onClose={() => setGalleryVisible(false)}
                        allLabel={allLabel}
                        isRoomDetail={!!galleryRoomDetail}
                        theme={appEnabledTheme}
                        move={enableRebranding}
                      />
                    </GalleryV2MobileContainer>
                  ) : (
                    <GalleryV2Container>
                      <ImageGalleryDesktop
                        images={
                          !galleryRoomDetail
                            ? galleryV2
                            : galleryRoomDetail.gallery
                        }
                        hotelDetail={
                          !galleryRoomDetail && {
                            name: hotelName,
                            rating: {
                              rating: Number(starRating),
                            },
                            review: {
                              rating: guestRating,
                              count: guestRatingCount.toString(),
                              message: guestRatingProps.ratingLabel,
                            },
                          }
                        }
                        allLabel={allLabel}
                        selectRoomLabel={selectRoomLabel}
                        onSelectRoom={() => {
                          setGalleryVisible(false);
                          setScrollToRoomList(true);
                        }}
                        onClose={() => setGalleryVisible(false)}
                        customProperties={{
                          isHorizontalScroll: !enableRebranding,
                        }}
                        roomDetail={galleryRoomDetail && galleryRoomDetail}
                        theme={appEnabledTheme}
                        move={enableRebranding}
                      />
                    </GalleryV2Container>
                  )}
                </PhoenixTheme>

                <GalleryContainer>
                  <GalleryHeader>
                    <Wrapper
                      display="flex"
                      flexDirection="row-reverse"
                      height="40px"
                      alignItems="center"
                    >
                      <CloseIcon
                        onClick={() => {
                          setGalleryVisible(false);
                        }}
                      />
                    </Wrapper>
                    <Wrapper
                      width={isMobileView ? '100%' : '648px'}
                      margin="0 auto 16px auto"
                    >
                      <Text
                        color="primaryWhite"
                        fontWeight="500"
                        fontSize="20px"
                        lineHeight="24px"
                      >
                        {translate('common.imagesFrom', {
                          hotelName,
                        })}
                      </Text>
                    </Wrapper>
                  </GalleryHeader>
                  <PhoenixTheme theme={appEnabledTheme}>
                    <Gallery
                      images={gallery}
                      imageWidth={isMobileView ? '100%' : '648px'}
                      imageHeight={isMobileView ? '205px' : '405px'}
                      smallCaption={isMobileView}
                    />
                  </PhoenixTheme>
                </GalleryContainer>
              </>
            </Modal>
          )}
          {isMapModalOpen && (
            <Modal modalIsOpen={isMapModalOpen} onClose={() => {}}>
              <>
                <MapModal
                  theme={appEnabledTheme}
                  move={enableRebranding}
                  isMobileView={isMobileView}
                  onCloseModal={closeMapModal}
                  filter={filter}
                  hotel={selectedHotel}
                  tripAdvisorData={tripAdvisorData}
                  isLoggedIn={isLoggedIn}
                  isLoggedUser={isLoggedUser}
                  credential={credential}
                  onClickSelectRoom={() => {
                    closeMapModal();
                    setScrollToRoomList(true);
                  }}
                  trackHotelMapSelectARoomWeb={trackHotelMapSelectARoomWeb}
                  GoogleMapsColoredFlag={GoogleMapsColoredFlag}
                />
              </>
            </Modal>
          )}
          {isMobileView && isPOIModalOpen && (
            <Modal modalIsOpen={isPOIModalOpen} onClose={() => {}}>
              <>
                <POIModalContent
                  theme={appEnabledTheme}
                  move={enableRebranding}
                  pointsOfInterest={pointsOfInterest}
                  isMobileView={isMobileView}
                  title={translate('hotelDetailsPage.poi.title')}
                  hotelName={hotelName}
                  hotelImageUrl={image}
                  onCloseModal={closePOIModal}
                />
              </>
            </Modal>
          )}
          {isFilterInit && (
            <FeedbackWrapper
              theme={appEnabledTheme}
              move={enableRebranding}
              contentEndpointPath={`${constants.HOTELS_FEEDBACK_PATH.HOTELS_ROOM_SELECT.replace(
                '{{locale}}',
                filter.languageId || constants.LANGUAGE_DEFAULT
              )}`}
              locale={filter.languageId || constants.LANGUAGE_DEFAULT}
              productType={
                constants.HOTELS_CHILLI_CHANNEL.HOTELS_ROOM_SELECT.type
              }
              chilliGroupId={constants.HOTELS_CHILLI_CHANNEL.GroupID}
              chilliTopicId={
                constants.HOTELS_CHILLI_CHANNEL.HOTELS_ROOM_SELECT.id
              }
              isMobile={isMobileView}
              geolocation={
                filter.country_code || constants.COUNTRY_CODE_DEFAULT
              }
            />
          )}
        </Container>
        {defaultLocaleUsed && isShowFallBackMessage && (
          <FallBackMessage
            message={translate('error.resourceNotFound')}
            theme={appEnabledTheme}
            move={enableRebranding}
          />
        )}
        <ScrollUpBtnStyle
          show={
            countOpts > MAX_OPTIONS_VIEW_SCROLL_UP &&
            pageY > SCROLL_POINT_VIEW_SCROLL_UP
          }
        >
          <PhoenixTheme theme={appEnabledTheme}>
            <ScrollUpButton
              stopPosition={100}
              style={{
                bottom: '44px',
                right: rightPosCrollUpBtn,
                boxShadow: true,
                hoverBackgroundColor: 'default',
                width: '44px',
                height: '44px',
                zIndex: 9,
              }}
            ></ScrollUpButton>
          </PhoenixTheme>
        </ScrollUpBtnStyle>
      </PreCheckout>
    </PhoenixTheme>
  );
};

HotelDetail.propTypes = {
  filter: object,
  hotelDetail: object,
  isFetching: bool,
  isFailed: bool,
  fetchHotelDetail: func,
  fetchGuestReview: func,
  guestReviews: object,
  fetchHotelRooms: func,
  clearHotelDetail: func,
  trackMobileBackButton: func,
  trackHotelShowMoreDescriptionClicked: func,
  trackHotelDetailedReview: func,
  trackHotelDetailedReviewPagination: func,
  trackHotelShowMorePolicyClicked: func,
  trackHotelRoomSelected: func,
  trackProductToCheckout: func,
  featureFlags: object,
  updateHotelFilter: func,
  trackReceivedFirstByte: func,
  trackFirstRender: func,
  trackContentDownloadCompleted: func,
  defaultLocaleUsed: bool,
  updateHotelDetail: bool,
  trackHotelSearchWidget: func,
  uhswData: object,
  updateBigPointFilter: func,
  bigMembership: object,
  bigpoints: object,
  trackHotelSeeOnAMapWeb: func,
  trackHotelShowMapWeb: func,
  trackHotelMapSelectARoomWeb: func,
  trackErrorPopOutRoom: func,
  trackViewImageBanner: func,
  trackViewRoom: func,
  trackHotelReviewClick: func,
  trackHotelRoomFilterUse: func,
};

export default HotelDetail;
